import { Link, useParams } from "react-router-dom";
import { Alert } from "../components/ui/Alert";
import { BANNER_BOTTOM_PADDING } from "../components/ui/BugReporterBanner";
import { Spinner } from "../components/ui/Spinner";
import { useVerifyInvite } from "../hooks/api/useInvites";

export function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const { data, isLoading, error } = useVerifyInvite(token);

  const handleGoogleLogin = () => {
    // 招待トークンを state パラメータに含めてGoogle認証を開始
    window.location.href = `${import.meta.env.VITE_API_URL || ""}/api/auth/google?state=invite:${token}`;
  };

  // ローディング中
  if (isLoading) {
    return (
      <div
        className={`flex min-h-screen items-center justify-center bg-gray-100 ${BANNER_BOTTOM_PADDING}`}
      >
        <div className="flex flex-col items-center gap-4">
          <Spinner size="lg" className="text-primary-600" />
          <p className="text-gray-600">招待リンクを確認中...</p>
        </div>
      </div>
    );
  }

  // エラー時
  if (error || !data) {
    return (
      <div
        className={`flex min-h-screen items-center justify-center bg-gray-100 ${BANNER_BOTTOM_PADDING}`}
      >
        <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-lg">
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold text-gray-900">
              座席管理システム
            </h1>
          </div>
          <Alert variant="error" title="エラー">
            招待リンクの確認中にエラーが発生しました。
          </Alert>
          <div className="mt-6 text-center">
            <Link
              to="/auth/login"
              className="text-sm text-primary-600 hover:text-primary-500"
            >
              ログインページに戻る
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // 無効な招待リンク
  if (!data.valid) {
    const errorMessages: Record<string, string> = {
      already_used: "この招待リンクは既に使用されています。",
      expired: "この招待リンクは期限切れです。",
      revoked: "この招待リンクは取り消されています。",
    };

    const errorMessage = data.reason
      ? (errorMessages[data.reason] ?? "この招待リンクは無効です。")
      : "この招待リンクは無効です。";

    return (
      <div
        className={`flex min-h-screen items-center justify-center bg-gray-100 ${BANNER_BOTTOM_PADDING}`}
      >
        <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-lg">
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold text-gray-900">
              座席管理システム
            </h1>
          </div>
          <Alert variant="error" title="招待リンクが無効です">
            {errorMessage}
            <p className="mt-2">
              管理者に新しい招待リンクを発行してもらってください。
            </p>
          </Alert>
          <div className="mt-6 text-center">
            <Link
              to="/auth/login"
              className="text-sm text-primary-600 hover:text-primary-500"
            >
              ログインページに戻る
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // 有効な招待リンク
  return (
    <div
      className={`flex min-h-screen items-center justify-center bg-gray-100 ${BANNER_BOTTOM_PADDING}`}
    >
      <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-lg">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900">座席管理システム</h1>
          <p className="mt-2 text-gray-600">招待を受けてアカウントを作成</p>
        </div>

        <Alert variant="info" className="mb-6">
          この招待リンクは有効です。Googleアカウントでログインしてアカウントを作成してください。
          {data.expiresAt && (
            <p className="mt-1 text-xs">
              有効期限: {new Date(data.expiresAt).toLocaleDateString("ja-JP")}
            </p>
          )}
        </Alert>

        <button
          type="button"
          onClick={handleGoogleLogin}
          className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-3 text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" aria-label="Google">
            <title>Google</title>
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          <span>Google でアカウントを作成</span>
        </button>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            既にアカウントをお持ちの方は
            <Link
              to="/auth/login"
              className="ml-1 text-primary-600 hover:text-primary-500"
            >
              ログイン
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
